import React, {useCallback, useState} from 'react';
import {useGridApiRef} from '@mui/x-data-grid-pro';
import {QueryStatus} from '@reduxjs/toolkit/query/react';
import {useNavigate} from 'react-router';
import {Concept} from 'types';
import type {RowSelectionModel} from 'components/dataTable/types';
import type {NorafUpdate} from 'services/agent/types';
import {useLocalization} from 'localization';
import {
  useGetNorafUpdatesQuery,
  useLazyGetNorafUpdatesQuery,
} from 'services/agent';
import {createAgentLink, useSetAgentItems} from 'services/agentItems';
import {useDataGridSettings} from 'services/settings/hooks';
import {FlexBox, Spacer} from 'components';
import {useDataTablePagination} from 'components/dataTable/hooks';
import {Table} from 'schema';
import {useSchema} from 'schemas';
import {useTableConfiguration} from 'schemas/hooks/useTableConfiguration';
import {NorafChangesSelectionActions} from './NorafChangesSelectionActions';

export const NorafChangesList = () => {
  const navigate = useNavigate();
  const {t} = useLocalization();
  const schema = useSchema(Concept.norafUpdate);
  const configuration = useTableConfiguration(Concept.norafUpdate);
  const apiRef = useGridApiRef();
  const {initialState, saveState} = useDataGridSettings(schema.name);
  const {paginationModel, onPaginationModelChange} = useDataTablePagination(
    apiRef,
    initialState?.pagination?.paginationModel,
  );
  const {data, status, refetch} = useGetNorafUpdatesQuery({
    page: paginationModel.page,
    size: paginationModel.pageSize,
  });
  const [trigger] = useLazyGetNorafUpdatesQuery();

  const [selection, setSelection] = useState<RowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });

  const setAgentItems = useSetAgentItems();
  const handleRowClick = useCallback(
    (row: NorafUpdate) => {
      const valueAsLink = createAgentLink(row, '');
      setAgentItems([]);

      // Initiates promise, which will update redux in the background.
      trigger({page: 0, size: 200})
        .unwrap()
        .then(norafUpdates => {
          const agentLinks =
            norafUpdates.items.map(norafUpdate =>
              createAgentLink(norafUpdate, t('page.agents.next.tooltip')),
            ) ?? [];

          // Remove all items before the one we clicked.
          const currentIndex = agentLinks.findIndex(
            l => l.link === valueAsLink.link,
          );

          // +1 to remove the one we navigated to.
          agentLinks.splice(0, currentIndex + 1);
          // Store in redux.
          setAgentItems(agentLinks);
        });

      // Navigate to clicked item.
      navigate(valueAsLink.link);
    },
    [navigate, setAgentItems, t, trigger],
  );

  return (
    <FlexBox>
      <Spacer height={2} />
      <Table
        schema={schema}
        data={data?.items ?? []}
        configuration={configuration}
        loading={status === QueryStatus.pending}
        header={t('page.agents.noraf.changes.title')}
        rowCount={data?.total ?? 0}
        onRowClick={handleRowClick}
        selection={selection}
        onSelectionChanged={setSelection}
        apiRef={apiRef}
        initialState={initialState}
        onSaveState={saveState}
        onPaginationModelChange={onPaginationModelChange}
      />

      <Spacer height={2} />

      <NorafChangesSelectionActions
        selection={selection}
        items={data?.items ?? []}
        postProcessing={() => {
          refetch();
        }}
        postSuccess={() => {
          setSelection({
            type: 'include',
            ids: new Set(),
          });
        }}
      />
    </FlexBox>
  );
};
